import React from "react";
import type {
  BotAuditLogPageDto,
  BotAuditLogRowDto,
} from "../models/edgeMonitorAdminModels";

const FONT_SORA = "'Sora', system-ui, -apple-system, sans-serif";
const FONT_MONO = "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace";
const FONT_INTER = "'Inter', system-ui, -apple-system, sans-serif";

/**
 * Edge-Live-Monitor editorial palette (matches Zone1Header / Zone3EventStream).
 * Deliberately NOT the redesignTokens dt — this internal staff page lives
 * outside the customer redesign tree and uses warm neutrals + terracotta.
 */
const COLORS = {
  border: "#E5E2D9",
  borderSoft: "#EFEDE5",
  rowBgEven: "#FAFAF7",
  rowBgOdd: "#FFFFFF",
  rowHover: "#F2EFE5",
  primaryText: "#1A1A1A",
  dimText: "#6B6B6B",
  mutedText: "#8A857B",
  panelBg: "#FFFFFF",
  headerBg: "#F0EFE9",
  green: "#2D8C4E",
  greenBg: "#E8EFE6",
  amber: "#9C7A0F",
  amberBg: "#F8F0DD",
  terracotta: "#D4572B",
  terracottaBg: "#FCEBE3",
  neutralBg: "#EDEAE0",
  neutralText: "#6B6B6B",
  staffProbeBg: "#EDE6F5",
  staffProbeText: "#5B3F8A",
  buttonBg: "#1A1F1A",
  buttonText: "#FAFAF7",
  buttonHoverBg: "#2A322A",
};

export interface Zone6AuditLogTableProps {
  data: BotAuditLogPageDto | null | undefined;
  loadingNoData: boolean;
  notAvailable: boolean;
  /** ISO timestamp of the last successful refresh (or null when never fetched). */
  lastFetchedAt: string | null;
  autoRefresh: boolean;
  onToggleAutoRefresh: (next: boolean) => void;
  onRefresh: () => void;
}

// ---------------------------------------------------------------------------
// Status / endpoint visuals

interface StatusVisual {
  bg: string;
  text: string;
  label: string;
}

/**
 * Status-code colour rules per spec §3.3:
 *   2xx → green
 *   404 with errorCode CATALOG_NOT_AVAILABLE (or just 404) → neutral grey
 *   4xx (other) → amber
 *   5xx → terracotta
 *
 * The 404-neutral rule is critical: SBL500 returns 404 CATALOG_NOT_AVAILABLE
 * as the *correct* response for `symbol-catalog`. Painting it red would have
 * staff chasing a non-bug.
 */
const statusVisual = (
  code: number,
  errorCode: string | null
): StatusVisual => {
  if (code >= 200 && code < 300) {
    return { bg: COLORS.greenBg, text: COLORS.green, label: String(code) };
  }
  if (code === 404) {
    return { bg: COLORS.neutralBg, text: COLORS.neutralText, label: "404" };
  }
  if (code >= 500) {
    return { bg: COLORS.terracottaBg, text: COLORS.terracotta, label: String(code) };
  }
  if (code >= 400) {
    return { bg: COLORS.amberBg, text: COLORS.amber, label: String(code) };
  }
  return { bg: COLORS.neutralBg, text: COLORS.neutralText, label: String(code) };
};

const parseUtcLoose = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const ms = Date.parse(hasTz ? iso : iso + "Z");
  return Number.isNaN(ms) ? null : ms;
};

const formatTimestamp = (iso: string): string => {
  const t = parseUtcLoose(iso);
  if (t === null) return "—";
  try {
    return new Date(t).toISOString().slice(11, 19);
  } catch {
    return "—";
  }
};

const formatRelative = (iso: string): string => {
  const t = parseUtcLoose(iso);
  if (t === null) return "";
  const ms = Date.now() - t;
  if (ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

// ---------------------------------------------------------------------------
// Row

const truncate = (s: string | null | undefined, n = 8): string => {
  if (!s) return "—";
  if (s.length <= n) return s;
  return s.slice(0, n);
};

interface RowProps {
  row: BotAuditLogRowDto;
  isEven: boolean;
}

const AuditRow: React.FC<RowProps> = ({ row, isEven }) => {
  const sv = statusVisual(row.statusCode, row.errorCode);
  const isStaffProbe = row.botKeyPrefix === "staff-probe";
  const symbolDetail =
    row.cachedSymbolCount != null && row.onDemandSymbolCount != null
      ? `${row.cachedSymbolCount + row.onDemandSymbolCount} sym (${row.cachedSymbolCount} cache · ${row.onDemandSymbolCount} edge)`
      : null;

  return (
    <tr
      style={{
        background: isEven ? COLORS.rowBgEven : COLORS.rowBgOdd,
      }}
    >
      {/* Timestamp — fixed width so adjacent columns do not jitter */}
      <td
        style={{
          ...cellBase,
          fontFamily: FONT_MONO,
          fontSize: "12px",
          color: COLORS.primaryText,
          width: "120px",
          minWidth: "120px",
          whiteSpace: "nowrap",
        }}
        title={`${row.timestamp} (${formatRelative(row.timestamp)})`}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
          <span>{formatTimestamp(row.timestamp)}</span>
          <span
            style={{
              fontSize: "10px",
              color: COLORS.mutedText,
              letterSpacing: "0.02em",
            }}
          >
            {formatRelative(row.timestamp)}
          </span>
        </div>
      </td>

      {/* Endpoint */}
      <td
        style={{
          ...cellBase,
          fontFamily: FONT_SORA,
          fontSize: "13px",
          color: COLORS.primaryText,
          fontWeight: 500,
        }}
      >
        {row.endpoint}
      </td>

      {/* Status — fixed width so it never reflows on different code lengths */}
      <td
        style={{
          ...cellBase,
          width: "92px",
          minWidth: "92px",
        }}
      >
        <span
          style={{
            display: "inline-block",
            padding: "3px 10px",
            borderRadius: "12px",
            background: sv.bg,
            color: sv.text,
            fontFamily: FONT_MONO,
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.04em",
            minWidth: "44px",
            textAlign: "center",
          }}
        >
          {sv.label}
        </span>
      </td>

      {/* Latency — fixed width, mono right-aligned */}
      <td
        style={{
          ...cellBase,
          width: "84px",
          minWidth: "84px",
          fontFamily: FONT_MONO,
          fontSize: "12px",
          color: COLORS.primaryText,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {row.latencyMs}
        <span
          style={{
            color: COLORS.mutedText,
            fontSize: "10px",
            marginLeft: "2px",
            letterSpacing: "0.04em",
          }}
        >
          ms
        </span>
      </td>

      {/* Error code */}
      <td
        style={{
          ...cellBase,
          fontFamily: FONT_MONO,
          fontSize: "11px",
          color: row.errorCode ? COLORS.terracotta : COLORS.mutedText,
          letterSpacing: "0.02em",
        }}
      >
        {row.errorCode ?? "—"}
      </td>

      {/* Bot key prefix — flag staff-probes visually */}
      <td style={{ ...cellBase }}>
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: "10px",
            background: isStaffProbe ? COLORS.staffProbeBg : COLORS.headerBg,
            color: isStaffProbe ? COLORS.staffProbeText : COLORS.dimText,
            fontFamily: FONT_MONO,
            fontSize: "11px",
            letterSpacing: "0.02em",
          }}
        >
          {row.botKeyPrefix}
        </span>
      </td>

      {/* User / customer truncated */}
      <td
        style={{
          ...cellBase,
          fontFamily: FONT_MONO,
          fontSize: "11px",
          color: COLORS.dimText,
          letterSpacing: "0.02em",
        }}
        title={`User: ${row.userId}\nCustomer: ${row.customerId}`}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
          <span>u·{truncate(row.userId)}</span>
          <span style={{ color: COLORS.mutedText, fontSize: "10px" }}>
            c·{truncate(row.customerId)}
          </span>
        </div>
      </td>

      {/* Symbol counts (read-tags only) */}
      <td
        style={{
          ...cellBase,
          fontFamily: FONT_MONO,
          fontSize: "11px",
          color: COLORS.dimText,
          letterSpacing: "0.02em",
        }}
      >
        {symbolDetail ?? <span style={{ color: COLORS.mutedText }}>—</span>}
      </td>
    </tr>
  );
};

const cellBase: React.CSSProperties = {
  padding: "8px 12px",
  borderBottom: `1px solid ${COLORS.borderSoft}`,
  verticalAlign: "top",
};

const headerCellBase: React.CSSProperties = {
  padding: "9px 12px",
  background: COLORS.headerBg,
  borderBottom: `1px solid ${COLORS.border}`,
  fontFamily: FONT_MONO,
  fontSize: "10px",
  fontWeight: 600,
  letterSpacing: "0.08em",
  color: COLORS.dimText,
  textTransform: "uppercase",
  textAlign: "left",
};

// ---------------------------------------------------------------------------
// Component

const Zone6AuditLogTable: React.FC<Zone6AuditLogTableProps> = ({
  data,
  loadingNoData,
  notAvailable,
  lastFetchedAt,
  autoRefresh,
  onToggleAutoRefresh,
  onRefresh,
}) => {
  const rows = data?.rows ?? [];
  const showEmpty = !loadingNoData && !notAvailable && rows.length === 0;

  return (
    <section
      style={{
        background: COLORS.panelBg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: "10px",
        margin: "0 32px 32px",
        overflow: "hidden",
        fontFamily: FONT_INTER,
      }}
      aria-label="Bot API audit log"
    >
      {/* Header strip — title + auto-refresh toggle + manual refresh */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          padding: "14px 18px 14px 20px",
          borderBottom: `1px solid ${COLORS.border}`,
          background: COLORS.panelBg,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: "10px",
              letterSpacing: "0.14em",
              color: COLORS.mutedText,
              textTransform: "uppercase",
              fontWeight: 500,
            }}
          >
            Zone 6 · Bot API audit log
          </div>
          <div
            style={{
              fontFamily: FONT_SORA,
              fontSize: "15px",
              fontWeight: 600,
              color: COLORS.primaryText,
              letterSpacing: "-0.005em",
            }}
          >
            Recent bot calls for this machine
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              fontFamily: FONT_MONO,
              fontSize: "11px",
              color: COLORS.dimText,
              letterSpacing: "0.04em",
              userSelect: "none",
              minHeight: "32px", // touch target safety on tablet
            }}
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => onToggleAutoRefresh(e.target.checked)}
              style={{
                width: "14px",
                height: "14px",
                cursor: "pointer",
                accentColor: "#1A1F1A",
              }}
            />
            <span>Auto-refresh · 10s</span>
          </label>

          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: "11px",
              color: COLORS.mutedText,
              letterSpacing: "0.02em",
            }}
          >
            {lastFetchedAt
              ? `updated ${formatRelative(lastFetchedAt)}`
              : "never updated"}
          </span>

          <button
            type="button"
            onClick={onRefresh}
            style={{
              padding: "6px 14px",
              borderRadius: "10px",
              background: COLORS.buttonBg,
              color: COLORS.buttonText,
              border: "none",
              fontFamily: FONT_SORA,
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "0.02em",
              cursor: "pointer",
              minHeight: "32px",
              transition: "background 120ms ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = COLORS.buttonHoverBg;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = COLORS.buttonBg;
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Table area */}
      <div style={{ maxHeight: "520px", overflow: "auto" }}>
        {loadingNoData ? (
          <div
            style={{
              padding: "48px 24px",
              textAlign: "center",
              fontFamily: FONT_MONO,
              fontSize: "12px",
              color: COLORS.mutedText,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Loading audit log…
          </div>
        ) : notAvailable && rows.length === 0 ? (
          <div
            style={{
              padding: "48px 24px",
              textAlign: "center",
              fontFamily: FONT_MONO,
              fontSize: "12px",
              color: COLORS.mutedText,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Admin only · no access
          </div>
        ) : showEmpty ? (
          <div
            style={{
              padding: "48px 24px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: "10px",
                color: COLORS.mutedText,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 500,
              }}
            >
              No bot calls yet
            </div>
            <div
              style={{
                fontFamily: FONT_INTER,
                fontSize: "13px",
                color: COLORS.dimText,
                lineHeight: "18px",
                maxWidth: "360px",
              }}
            >
              Trigger a bot call against this machine (e.g. via MIDAS) and the
              audit row will appear here within ~10s.
            </div>
          </div>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "auto",
            }}
          >
            <thead>
              <tr>
                <th style={{ ...headerCellBase, width: "120px", minWidth: "120px" }}>
                  Time
                </th>
                <th style={{ ...headerCellBase }}>Endpoint</th>
                <th style={{ ...headerCellBase, width: "92px", minWidth: "92px" }}>
                  Status
                </th>
                <th
                  style={{
                    ...headerCellBase,
                    width: "84px",
                    minWidth: "84px",
                    textAlign: "right",
                  }}
                >
                  Latency
                </th>
                <th style={{ ...headerCellBase }}>Error</th>
                <th style={{ ...headerCellBase }}>Key</th>
                <th style={{ ...headerCellBase }}>User · Customer</th>
                <th style={{ ...headerCellBase }}>Symbols</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <AuditRow key={row.id} row={row} isEven={i % 2 === 0} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer — count, limit hint */}
      {rows.length > 0 && (
        <div
          style={{
            padding: "8px 18px",
            borderTop: `1px solid ${COLORS.borderSoft}`,
            background: COLORS.headerBg,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontFamily: FONT_MONO,
            fontSize: "10px",
            color: COLORS.mutedText,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          <span>{rows.length} rows · last 50 · server clamps ≤ 200</span>
          <span>{autoRefresh ? "auto · 10s" : "manual"}</span>
        </div>
      )}
    </section>
  );
};

export default Zone6AuditLogTable;
